import * as React from "react";
import ReactDOMServer from "react-dom/server";
import LeafletMap from "./LeafletMap";
import { gettext } from "i18n";
import { BrowserRouter, Link } from "react-router-dom";
import "./MeasurementStationMap.scss";
import { useQuery } from "@apollo/client";
import { GetMeasurementStation_station_primary_datasource } from "utils/__generated__/GetMeasurementStation";
import { ALERT_LEVEL_RED } from "utils/stations";
import { GetMapLayersQuery } from "utils/__generated__/GetMapLayersQuery";
import {
  GET_MAP_LAYERS,
  ALERT_LEVEL_NONE,
  isFMIDataAvailable,
  useStationAlertLevels,
} from "utils/stations";

interface MapStation {
  id: number;
  identifier: string;
  name: string;
  lon: number;
  lat: number;
  status: string;
  visibility: string;
  origin: string;
  primary_datasource: GetMeasurementStation_station_primary_datasource | null;
  distance_alert_threshold?: number | null;
  last_distance_alert_timestamp?: string | null;
  fmi_station_networks: {
    fmi_network: {
      name: string;
      weather_enabled: boolean;
      code: string;
    };
  }[];
}
interface MeasurementStationMapProps {
  stations: MapStation[];
  height?: number | string;
  mapLayer?: string;
}

const alertEndDate = new Date();
const alertStartDate = new Date(+alertEndDate - 1 * 86400000);

const renderComponentToString = (
  Component: React.ComponentType<any>,
  props: any,
  children?: any
) => {
  return ReactDOMServer.renderToString(
    <BrowserRouter>
      <Component {...props}>{children}</Component>
    </BrowserRouter>
  );
};

const MeasurementStationMap = (props: MeasurementStationMapProps) => {
  const { stations, height = 400, mapLayer } = props;
  const { data: layerData } = useQuery<GetMapLayersQuery>(GET_MAP_LAYERS);

  const stationIds = stations.map((s) => s.id);
  const alertlevelPerStation = useStationAlertLevels({
    stationIds,
    startDate: alertStartDate,
    endDate: alertEndDate,
  });

  const distanceAlertsPerStation = React.useMemo(() => {
    return stations.reduce((acc, station) => {
      if (
        station.distance_alert_threshold &&
        station.last_distance_alert_timestamp &&
        new Date(station.last_distance_alert_timestamp) >= alertStartDate &&
        new Date(station.last_distance_alert_timestamp) <= alertEndDate
      ) {
        acc[station.id] = ALERT_LEVEL_RED;
      }
      return acc;
    }, {} as Record<number, number>);
  }, [stations]);

  const mapData = React.useMemo(
    () =>
      stations
        .filter((s) => {
          if (!s.lat || !s.lon) {
            return false;
          }
          // Only include weather_enabled stations
          return isFMIDataAvailable(s);
        })
        .map((s) => {
          const networkName = s.fmi_station_networks?.[0]?.fmi_network?.name;
          const stationName = networkName
            ? `${s.name} (${gettext(networkName)})`
            : s.name;

          const renderedTitle = renderComponentToString(
            Link,
            {
              to: `/stations/${s.identifier}`,
            },
            stationName
          );
          return {
            lon: s.lon,
            lat: s.lat,
            id: s.identifier,
            title: renderedTitle,
            status: s.status,
            public: s.visibility === "public",
            primarySourceName: s.primary_datasource?.name ?? "",
            primarytUnit: s.primary_datasource?.unit?.symbol ?? "",
            alertLevel:
              alertlevelPerStation[s.id] ??
              distanceAlertsPerStation[s.id] ??
              ALERT_LEVEL_NONE,
          };
        }),
    [stations, alertlevelPerStation, distanceAlertsPerStation]
  );

  if (!mapData.length) {
    return <div>Ei mittausasemia</div>;
  }

  const privateStations = mapData.filter((s) => !s.public);
  if (mapData.length > 1 && !privateStations.length) {
    return <div>Ei mittausasemia.</div>;
  }

  return (
    <div>
      {!!layerData && (
        <LeafletMap
          data={mapData}
          style={{ height }}
          mapLayer={mapLayer}
          layerData={layerData}
          center={
            stations[0]?.lat && stations[0]?.lon
              ? [stations[0].lat, stations[0].lon]
              : [65.012615, 25.471453]
          }
          zoom={3}
          scrollWheelZoom={false}
        />
      )}
    </div>
  );
};
export default MeasurementStationMap;
